import { useEffect, useMemo, useRef } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { extractantFactorsKey } from "./useExtractantFactors";
import {
  jdOrgIdOf,
  jdOrgIdOfField,
  jdFieldIdOf,
  jdNameOf,
} from "../lib/jdShape";
import { api, ApiError } from "../api";
import { getActiveOrg } from "../lib/activeOrg";
import { useOrgStore } from "../stores/orgStore";
import type { SamplingOrder, SamplingOrderResults } from "../api/client";
import { newlyCompleted, pollIntervalFor } from "../lib/fieldJobs";
import {
  invalidateFinishedJobs,
  jobsThatLeftActiveSet,
  refreshConfirmedCompletions,
} from "../lib/jobRefresh";
import { sessionPollInterval } from "../lib/bulkIngest";
import { haneyFromWire, haneyToWire, type HaneyPoint } from "../lib/haney";
import { labSummary } from "../lib/labTests";
import { loadLocalHaney, saveLocalHaney } from "../stores/haneyStore";
import {
  LAYER_DETAIL_GC_MS,
  LAYER_LIST_STALE_MS,
  layerDetailKey,
  layerListKey,
  layersFromDocument,
  loadFieldLayerList,
  type LayerDetailRequest,
  type LayerListData,
} from "../lib/layerLoad";
import type {
  DashboardField,
  FieldJob,
  FieldSoilComposition,
  Folder,
  ManifestLayer,
} from "../types/contract";

// All server reads go through TanStack Query (Front-End.md: single server-state layer).
export const qk = {
  me: ["me"] as const,
  fields: ["fields"] as const,
  // Prefix. The live entry is ["rx-coverage", orgId]. Invalidation uses the prefix.
  rxCoverage: ["rx-coverage"] as const,
  folders: ["folders"] as const,
  field: (id: string) => ["field", id] as const,
  layers: (id: string) => ["layers", id] as const,
  // Children of `layers`, so a save/delete that invalidates the prefix
  // refreshes the list and every loaded detail.
  layerList: (id: string) => layerListKey(id),
  layerDetail: (fieldId: string, layerId: string, version: string) =>
    layerDetailKey(fieldId, layerId, version),
  covariates: (id: string) => ["covariates", id] as const,
  samples: (id: string) => ["samples", id] as const,
  sampleEvents: (id: string) => ["sample-events", id] as const,
  soilComposition: (id: string) => ["soil-composition", id] as const,
  haney: (id: string) => ["haney", id] as const,
  runningJobs: ["jobs", "running"] as const,
  jdAllFields: ["jd", "fields", "all"] as const,
  jdStatus: ["jd", "status"] as const,
  fieldJobs: (id: string) => ["field-jobs", id] as const,
  bulkSessions: ["bulk-sessions"] as const,
  bulkErrorsBadge: ["bulk-errors-badge"] as const,
  orgJobs: ["org-jobs"] as const,
  systemCounts: ["admin", "system-counts"] as const,
  adminUsers: ["admin", "users"] as const,
  adminOrgs: ["admin", "orgs"] as const,
  adminApiKeys: ["admin", "api-keys"] as const,
  adminApiScopes: ["admin", "api-keys", "scopes"] as const,
  opsManager: ["admin", "ops", "manager"] as const,
  opsProcesses: ["admin", "ops", "processes"] as const,
  adminJobs: ["admin", "ops", "jobs"] as const,
  bulkSession: (id: string) => ["bulk-session", id] as const,
  bulkObjects: (id: string) => ["bulk-objects", id] as const,
  bulkConflicts: (id: string) => ["bulk-conflicts", id] as const,
  workOrders: ["work-orders"] as const,
  extractantFactors: extractantFactorsKey,
};

// One soil-results delete refreshes the sample list, the event list (the
// current event can fall back), the sample IDW layers, and the field badges.
export function invalidateSoilSampleDelete(
  qc: QueryClient,
  fieldId: string,
): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: qk.sampleEvents(fieldId) }),
    qc.invalidateQueries({ queryKey: qk.samples(fieldId) }),
    qc.invalidateQueries({ queryKey: qk.layers(fieldId) }),
    qc.invalidateQueries({ queryKey: qk.fields }),
  ]);
}

// List reads are scoped to one org. The prefix (`qk.fields`, `qk.folders`, …)
// stays the invalidation key; the live cache entry appends the org id.
export function orgScopedKey<T extends readonly unknown[]>(
  prefix: T,
  orgId: string,
): readonly [...T, string] {
  return [...prefix, orgId];
}

function useActiveOrgId(): string | null {
  return useOrgStore((s) => s.activeOrgId);
}

function orgQueryKey<T extends readonly unknown[]>(
  prefix: T,
  orgId: string | null,
): T | readonly [...T, string] {
  return orgId ? orgScopedKey(prefix, orgId) : prefix;
}

function liveKey<T extends readonly unknown[]>(prefix: T) {
  const id = useOrgStore.getState().activeOrgId ?? getActiveOrg();
  return id ? orgScopedKey(prefix, id) : null;
}

export function readOrgScoped<T>(
  qc: QueryClient,
  prefix: readonly unknown[],
): T | undefined {
  const key = liveKey(prefix);
  if (key) {
    const scoped = qc.getQueryData<T>(key);
    if (scoped !== undefined) return scoped;
  }
  return qc.getQueryData<T>(prefix);
}

// All work orders across the org's fields (Work orders page): soil-sampling (SS4) AND
// EC-scan (EC2) reads are per-field, so fan out over the fields list and tag each order
// with its field name. A field whose read fails contributes nothing rather than sinking
// the whole page. `detail` carries the order's sub-type: the sampling kind
// (points/zones) or the EC scan pattern.
export interface WorkOrderRow {
  request_id: string;
  kind: "sampling" | "ec";
  field_id: string;
  field_name: string;
  status: string;
  created_at: string | null;
  point_count: number | null;
  detail: string | null;
  lab: string | null; // lab-test summary (panel + Haney freq) — sampling orders only.
  design_event_id: string | null;
  results: SamplingOrderResults | null;
  config: SamplingOrder["config"];
}

export function useWorkOrders(enabled: boolean) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgQueryKey(qk.workOrders, orgId),
    enabled: enabled && !!orgId,
    queryFn: async (): Promise<WorkOrderRow[]> => {
      const fields = await api.fetchFields();
      const per = await Promise.all(
        fields.map(async (f) => {
          const base = { field_id: f.field_id, field_name: f.field_name };
          const [sampling, ec] = await Promise.all([
            api.fetchSamplingOrders(f.field_id).catch(() => []),
            api.fetchEcScanOrders(f.field_id).catch(() => []),
          ]);
          return [
            ...sampling.map((o) => ({
              ...base,
              kind: "sampling" as const,
              request_id: o.request_id,
              status: o.status,
              created_at: o.created_at,
              point_count: Array.isArray(o.config?.points)
                ? o.config.points.length
                : Array.isArray(o.config?.cores)
                  ? o.config.cores.length
                  : null,
              detail:
                Array.isArray(o.config?.cores) && o.config.cores.length
                  ? `${o.config?.kind ?? "zones"} · ${o.config.cores.length} core${o.config.cores.length === 1 ? "" : "s"}`
                  : (o.config?.kind ?? null),
              lab: o.config?.lab ? labSummary(o.config.lab) : null,
              design_event_id: o.design_event_id,
              results: o.results,
              config: o.config ?? null,
            })),
            ...ec.map((o) => ({
              ...base,
              kind: "ec" as const,
              request_id: o.request_id,
              status: o.status,
              created_at: o.created_at,
              point_count: null,
              detail: o.pattern,
              lab: null,
              design_event_id: null,
              results: null,
              config: null,
            })),
          ];
        }),
      );
      // newest first; undated rows sink to the bottom
      return per
        .flat()
        .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    },
  });
}

export function useFields() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgQueryKey(qk.fields, orgId),
    queryFn: () => api.fetchFields(),
    enabled: !!orgId,
  });
}

// One org-wide read is the dashboard's source for "Needs Rx". The client default
// turns window-focus refetch off; this screen needs it, because a save on another
// tab is otherwise invisible until staleTime passes and something else refetches.
// While the query is pending or in error the table shows no nutrient flags.
export function useRxCoverage() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgId ? ([...qk.rxCoverage, orgId] as const) : qk.rxCoverage,
    queryFn: () => api.fetchFertRxCoverage(),
    enabled: !!orgId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useFolders() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgQueryKey(qk.folders, orgId),
    queryFn: () => api.fetchFolders(),
    enabled: !!orgId,
  });
}

export function useFieldLayerList(fieldId: string | null) {
  return useQuery({
    queryKey: qk.layerList(fieldId ?? ""),
    enabled: !!fieldId,
    staleTime: LAYER_LIST_STALE_MS,
    queryFn: ({ client, signal, queryKey }) =>
      loadFieldLayerList(fieldId as string, {
        signal,
        previous: client.getQueryData<LayerListData>(queryKey),
        ifNoneMatch: client.getQueryData<LayerListData>(queryKey)?.etag,
      }),
  });
}

// One layer's payload. `staleTime: Infinity` plus the content version in the
// key means a saved layer is fetched again only when the list's version
// changes, or when a save/delete invalidates `qk.layers`. `gcTime` keeps it
// for the session so reopening the field does not hit the network.
export function useLayerData(
  fieldId: string | null,
  layerId: string | null,
  contentVersion: number | string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: qk.layerDetail(
      fieldId ?? "",
      layerId ?? "",
      contentVersion == null ? "" : String(contentVersion),
    ),
    enabled: Boolean(fieldId && layerId && enabled),
    staleTime: Infinity,
    gcTime: LAYER_DETAIL_GC_MS,
    queryFn: ({ signal }) =>
      api.fetchLayerDetail(fieldId as string, layerId as string, { signal }),
  });
}

export function useLayerDetails(
  fieldId: string | null,
  requests: readonly LayerDetailRequest[],
) {
  const results = useQueries({
    queries: requests.map((request) => ({
      queryKey: qk.layerDetail(
        fieldId ?? "",
        request.id,
        request.contentVersion == null ? "" : String(request.contentVersion),
      ),
      enabled: Boolean(fieldId && request.id),
      staleTime: Infinity,
      gcTime: LAYER_DETAIL_GC_MS,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.fetchLayerDetail(fieldId as string, request.id, { signal }),
    })),
  });
  const stamp = results
    .map(
      (result, index) =>
        `${requests[index]?.id ?? ""}:${result.dataUpdatedAt}:${result.status}`,
    )
    .join("|");
  const { byId, errors } = useMemo(() => {
    const next = new Map<string, ManifestLayer>();
    const failed = new Map<string, Error>();
    requests.forEach((request, index) => {
      const result = results[index];
      if (result?.data) next.set(request.id, result.data);
      else if (result?.error) failed.set(request.id, result.error);
    });
    return { byId: next, errors: failed };
    // `stamp` changes only when a detail settles or its id set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp]);
  return { byId, errors };
}

// Ids, titles, and legend ranges. No signed URL and no geometry except the
// field boundary, which the metadata document already carries.
export function useFieldLayers(fieldId: string | null) {
  const list = useFieldLayerList(fieldId);
  const data = useMemo(
    () => (list.data ? layersFromDocument(list.data.document) : undefined),
    [list.data],
  );
  return { ...list, data };
}

function featuresOfBoundary(
  geo: GeoJSON.GeoJSON,
  fieldId: string,
): GeoJSON.Feature[] {
  const src =
    geo.type === "FeatureCollection"
      ? geo.features
      : geo.type === "Feature"
        ? [geo]
        : [
            {
              type: "Feature" as const,
              geometry: geo,
              properties: {},
            },
          ];
  const features: GeoJSON.Feature[] = [];
  for (const feature of src) {
    const kind = feature.geometry?.type;
    if (kind !== "Polygon" && kind !== "MultiPolygon") continue;
    features.push({
      type: "Feature",
      geometry: feature.geometry,
      properties: { __field_id: fieldId },
    });
  }
  return features;
}

// Boundaries for a SET of fields at once: when the map is opened for several selected fields, show
// ALL their outlines — not just the focused one — so you can see where they all are.
export function useSelectionBoundaries(fieldIds: string[]) {
  const qc = useQueryClient();
  const key = [...fieldIds].sort().join(",");
  return useQuery({
    queryKey: ["selection-boundaries", key],
    enabled: fieldIds.length > 1,
    staleTime: LAYER_LIST_STALE_MS,
    queryFn: async (): Promise<GeoJSON.FeatureCollection> => {
      const features: GeoJSON.Feature[] = [];
      await Promise.all(
        fieldIds.map(async (fid) => {
          const listed = await qc
            .fetchQuery({
              queryKey: qk.layerList(fid),
              staleTime: LAYER_LIST_STALE_MS,
              queryFn: ({ client, signal, queryKey }) =>
                loadFieldLayerList(fid, {
                  signal,
                  previous: client.getQueryData<LayerListData>(queryKey),
                  ifNoneMatch:
                    client.getQueryData<LayerListData>(queryKey)?.etag,
                }),
            })
            .catch(() => null);
          const boundary = listed?.document.boundary;
          if (!boundary || typeof boundary !== "object") return;
          features.push(
            ...featuresOfBoundary(boundary as GeoJSON.GeoJSON, fid),
          );
        }),
      );
      return { type: "FeatureCollection", features };
    },
  });
}

// The field's covariate catalog (CV1) — only fetched when the Field data panel is open.
export function useFieldCovariates(fieldId: string | null) {
  return useQuery({
    queryKey: qk.covariates(fieldId ?? ""),
    queryFn: () => api.fetchFieldCovariates(fieldId as string),
    enabled: !!fieldId,
  });
}

// 404 (pre-build) -> null, so the groups simply don't appear rather than erroring.
export function useSoilComposition(fieldId: string | null) {
  return useQuery({
    queryKey: qk.soilComposition(fieldId ?? ""),
    enabled: !!fieldId,
    retry: false, // optional display-only layer — a missing endpoint stays quiet
    queryFn: async (): Promise<FieldSoilComposition | null> => {
      try {
        return await api.fetchSoilComposition(fieldId as string);
      } catch (e) {
        // A 404, a missing route, or a bad artifact all mean the same
        // thing for a display-only layer: hide the two groups, never surface an error. (A bare
        // 404 from a missing route throws a raw error, not an ERR_NOT_FOUND envelope — treat all
        // failures the same.) Log non-404s for dev visibility.
        if (!(e instanceof ApiError && e.code === "ERR_NOT_FOUND")) {
          console.debug("[soil-composition] unavailable:", e);
        }
        return null;
      }
    },
  });
}

// Imported Haney soil-test points — persisted server-side, with a localStorage
// fallback when that read fails (server-first, an import is not lost).
export function useHaneyPoints(fieldId: string | null) {
  return useQuery({
    queryKey: qk.haney(fieldId ?? ""),
    enabled: !!fieldId,
    queryFn: async (): Promise<HaneyPoint[]> => {
      try {
        return (await api.fetchHaneyPoints(fieldId as string)).map(
          haneyFromWire,
        );
      } catch (e) {
        console.debug(
          "[haney] server read unavailable — using local fallback",
          e,
        );
        return loadLocalHaney(fieldId as string);
      }
    },
  });
}

// Save (or clear, with ) the field's Haney points: write-through to localStorage + POST to the
// server; the query is invalidated so the read refreshes from whichever store is authoritative.
export function useSaveHaney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      fieldId,
      points,
    }: {
      fieldId: string;
      points: HaneyPoint[];
    }) => {
      saveLocalHaney(fieldId, points);
      try {
        await api.saveHaneyPoints(fieldId, points.map(haneyToWire));
      } catch (e) {
        console.debug("[haney] server save unavailable — kept local", e);
      }
      return { fieldId, points };
    },
    onSuccess: ({ fieldId }) =>
      qc.invalidateQueries({ queryKey: qk.haney(fieldId) }),
  });
}

// Both invalidate the events + the field's sample-derived reads, because the designation decides
// which samples a High-Res run fits and which land in "Old samples".
export function useDesignateCurrentEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      eventId,
    }: {
      fieldId: string;
      eventId: string | null;
    }) =>
      eventId
        ? api.designateCurrentEvent(fieldId, eventId)
        : api.resetCurrentEvent(fieldId),
    onSuccess: (_r, { fieldId }) => {
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.samples(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.layers(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.fields }); // "Needs Updated Soil Map" keys off the current set
    },
  });
}

// EVERY John Deere field the user can see, across ALL their JD orgs — Bulk Data Dump: JD Ops.
export interface JdFieldRow {
  id: string;
  /* * JD often ships only GUIDs; absent means "no readable name", so callers fall back to the id */
  name?: string;
  jdOrgId: string;
  orgName?: string;
  linkedFieldId?: string | null;
  linkedFieldName?: string | null;
}
/*
 *
 * A TOTAL failure must not look like ZERO.
 */
export interface JdFieldsResult {
  rows: JdFieldRow[];
  orgCount: number;
  failedOrgs: number;
}

export function useJdFields() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgQueryKey(qk.jdAllFields, orgId),
    enabled: !!orgId,
    queryFn: async (): Promise<JdFieldsResult> => {
      const orgs = (await api.jdOrgs()) as unknown as Record<string, unknown>[];
      let failedOrgs = 0;
      const per = await Promise.all(
        orgs.map(async (o): Promise<JdFieldRow[]> => {
          // Read the id the same permissive way the single-field picker does (lib/jdShape). Reading
          // only `jd_org_id` was THE bug: an org payload keyed `id` produced an undefined lookup.
          const jdOrgId = jdOrgIdOf(o);
          const orgName = jdNameOf(o);
          if (!jdOrgId) {
            failedOrgs += 1; // unusable shape is a FAILURE, not an org with no fields
            return [];
          }
          try {
            const fs = (await api.jdFields(jdOrgId)) as unknown as Record<
              string,
              unknown
            >[];
            return fs.map((f) => ({
              id: jdFieldIdOf(f),
              name: jdNameOf(f),
              // EXPLICIT org keys only — a bare `id` on a field payload is the FIELD, and sending that
              // as the org made every bring-in fail. The queried org is the trustworthy fallback.
              jdOrgId: jdOrgIdOfField(f) || jdOrgId,
              orgName,
              linkedFieldId:
                typeof f.linked_field_id === "string"
                  ? f.linked_field_id
                  : null,
              linkedFieldName:
                typeof f.linked_field_name === "string"
                  ? f.linked_field_name
                  : null,
            }));
          } catch {
            failedOrgs += 1;
            return [];
          }
        }),
      );
      if (orgs.length > 0 && failedOrgs === orgs.length) {
        throw new Error(
          "Couldn’t read your John Deere fields — the connection may have expired.",
        );
      }
      return {
        rows: per
          .flat()
          .filter((r) => r.id) // a row we cannot address is not offerable
          // Grouped by ORG, then by name: once the org is a visible column, a name-only sort
          // interleaves orgs and the column reads as noise.
          .sort(
            (a, b) =>
              (a.orgName ?? "").localeCompare(b.orgName ?? "") ||
              (a.name ?? a.id).localeCompare(b.name ?? b.id),
          ),
        orgCount: orgs.length,
        failedOrgs,
      };
    },
    retry: false,
  });
}

/* * JD connection status — `configured` = wired server-side, `connected` = THIS user has a token. */
export function useJdStatus(enabled = true) {
  return useQuery({
    queryKey: qk.jdStatus,
    queryFn: () => api.jdStatus(),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

// Poll every few seconds while a job is active, including a blocked job that can
// still be retried. Stop when the field is idle.
export function useFieldJobs(fieldId: string | null) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.fieldJobs(fieldId ?? ""),
    queryFn: () => api.fetchFieldJobs(fieldId as string),
    enabled: !!fieldId,
    retry: false,
    refetchInterval: (query) => pollIntervalFor(query.state.data),
  });
  const prevRef = useRef<FieldJob[] | undefined>(undefined);
  const prevFieldRef = useRef<string | null>(null);
  const { data } = q;
  useEffect(() => {
    if (prevFieldRef.current !== fieldId) {
      prevFieldRef.current = fieldId;
      prevRef.current = undefined; // field switch — never diff one field's jobs against another's
    }
    if (!data) return;
    const done = newlyCompleted(prevRef.current, data);
    prevRef.current = data;
    if (!done.length || !fieldId) return;
    // The field read keeps finished rows, so this transition is the completion
    // signal. Open screens refetch; a screen opened afterwards does not keep
    // the pre-job snapshot for its stale time.
    invalidateFinishedJobs(
      qc,
      fieldId,
      done.map((job) => job.process_id),
    );
  }, [data, fieldId, qc]);
  return q;
}

// The field's sample events (zones/points campaigns) — soil-samples flow + soilprops picker.
export function useSampleEvents(fieldId: string | null) {
  return useQuery({
    queryKey: qk.sampleEvents(fieldId ?? ""),
    queryFn: () => api.fetchSampleEvents(fieldId as string),
    enabled: !!fieldId,
  });
}

export function useSoilSamples(fieldId: string | null) {
  return useQuery({
    queryKey: qk.samples(fieldId ?? ""),
    queryFn: () => api.fetchSoilSamples(fieldId as string),
    enabled: !!fieldId,
  });
}

// ---- Mutations. Rename + move patch qk.fields OPTIMISTICALLY so inline-edit and
// drag-into-folder feel instant, rolling back on error. (Front-End.md §A9/§A10.) ----
function patchField(
  qc: ReturnType<typeof useQueryClient>,
  key: readonly unknown[],
  fieldId: string,
  patch: Partial<DashboardField>,
) {
  qc.setQueryData<DashboardField[]>(key, (old) =>
    old?.map((f) => (f.field_id === fieldId ? { ...f, ...patch } : f)),
  );
}

export function useRenameField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, name }: { fieldId: string; name: string }) =>
      api.renameField(fieldId, name),
    onMutate: async ({ fieldId, name }) => {
      await qc.cancelQueries({ queryKey: qk.fields });
      const key = liveKey(qk.fields);
      const prev = key ? qc.getQueryData<DashboardField[]>(key) : undefined;
      if (key) patchField(qc, key, fieldId, { field_name: name });
      return { prev, key };
    },
    onError: (_e, _v, ctx) =>
      ctx?.key && ctx.prev && qc.setQueryData(ctx.key, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.fields }),
  });
}

export function useMoveField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      folderId,
    }: {
      fieldId: string;
      folderId: string | null;
    }) => api.moveField(fieldId, folderId),
    onMutate: async ({ fieldId, folderId }) => {
      await qc.cancelQueries({ queryKey: qk.fields });
      const key = liveKey(qk.fields);
      const prev = key ? qc.getQueryData<DashboardField[]>(key) : undefined;
      if (key) patchField(qc, key, fieldId, { folder_id: folderId });
      return { prev, key };
    },
    onError: (_e, _v, ctx) =>
      ctx?.key && ctx.prev && qc.setQueryData(ctx.key, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.fields }),
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      parentFolderId,
    }: {
      name: string;
      parentFolderId?: string | null;
    }) => api.createFolder(name, parentFolderId ?? null),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.folders }),
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      api.renameFolder(folderId, name),
    onMutate: async ({ folderId, name }) => {
      await qc.cancelQueries({ queryKey: qk.folders });
      const key = liveKey(qk.folders);
      const prev = key ? qc.getQueryData<Folder[]>(key) : undefined;
      if (key)
        qc.setQueryData<Folder[]>(key, (old) =>
          old?.map((f) => (f.folder_id === folderId ? { ...f, name } : f)),
        );
      return { prev, key };
    },
    onError: (_e, _v, ctx) =>
      ctx?.key && ctx.prev && qc.setQueryData(ctx.key, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.folders }),
  });
}

export function useReparentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderId,
      parentFolderId,
    }: {
      folderId: string;
      parentFolderId: string | null;
    }) => api.reparentFolder(folderId, parentFolderId),
    onMutate: async ({ folderId, parentFolderId }) => {
      await qc.cancelQueries({ queryKey: qk.folders });
      const key = liveKey(qk.folders);
      const prev = key ? qc.getQueryData<Folder[]>(key) : undefined;
      if (key)
        qc.setQueryData<Folder[]>(key, (old) =>
          old?.map((f) =>
            f.folder_id === folderId
              ? { ...f, parent_folder_id: parentFolderId }
              : f,
          ),
        );
      return { prev, key };
    },
    onError: (_e, _v, ctx) =>
      ctx?.key && ctx.prev && qc.setQueryData(ctx.key, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.folders }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folderId: string) => api.deleteFolder(folderId),
    // deleting a folder reparents its children (folders + fields) server-side → refetch both
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.folders });
      qc.invalidateQueries({ queryKey: qk.fields });
    },
  });
}

// ---- Bulk ingest session (BE-Light rev sm-api-read-00103-xxv) ---------------
// Polls only while the SERVER is working. A session parked in an `awaiting_*` status is waiting on the person
// looking at the screen and cannot change on its own, so we stop rather than re-ask every 3s; submitting a
// decision invalidates the key and restarts the cycle.
// `data === null` is the real not-started-yet signal (we mint the session id before compute creates the row
// so that window is normal). `retry: false` matters more than usual here: BE-Light 503s rather than serving
// when the store is unreadable, and retrying would just delay showing the user a real problem.
export function useBulkSession(sessionId: string | null) {
  return useQuery({
    queryKey: qk.bulkSession(sessionId ?? ""),
    queryFn: () => api.bulkSession(sessionId as string),
    enabled: !!sessionId,
    retry: false,
    refetchInterval: (query) => sessionPollInterval(query.state.data?.status),
  });
}

export function useBulkObjects(
  sessionId: string | null,
  params: Parameters<typeof api.bulkObjects>[1] = {},
  enabled = true,
) {
  return useQuery({
    queryKey: [...qk.bulkObjects(sessionId ?? ""), params] as const,
    queryFn: () => api.bulkObjects(sessionId as string, params),
    enabled: !!sessionId && enabled,
    retry: false,
  });
}

export function useBulkSessions(enabled = true) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgQueryKey(qk.bulkSessions, orgId),
    queryFn: () => api.bulkSessions({ limit: 10 }),
    enabled: enabled && !!orgId,
    retry: false,
  });
}

/*
 *
 * The Errors-menu badge. `count === null` (or a failed read) renders NO badge — "absent, never 0": a
 * confident zero from a store we can't read is the same lie as an empty list. `mark-seen` is a monotonic
 * BOOKMARK (touches no error row); calling it and invalidating this query is how the count drops.
 */
export function useBulkErrorsBadge(enabled = true) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: orgQueryKey(qk.bulkErrorsBadge, orgId),
    queryFn: () => api.bulkErrorsCount(),
    enabled: enabled && !!orgId,
    retry: false,
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
}

/*
 *
 * BE-Light's `GET /api/compute-jobs` (`sm-api-read-00112-rk8`) returns the same columns as the
 * per-field read plus `field_id`, active-only by default; grouped here by field so each row cell is
 * a lookup.
 */
export function useOrgFieldJobs(enabled = true) {
  const qc = useQueryClient();
  const orgId = useActiveOrgId();
  const prevRef = useRef<Record<string, FieldJob[]> | undefined>(undefined);
  // Bumped when this read drops out (org switch, logout). An in-flight
  // confirm from the previous org must not refresh the next org's screens.
  const generation = useRef(0);
  const q = useQuery({
    queryKey: orgQueryKey(qk.orgJobs, orgId),
    enabled: enabled && !!orgId,
    retry: false,
    refetchInterval: 30_000,
    queryFn: async (): Promise<Record<string, FieldJob[]>> => {
      const rows = await api.fetchOrgJobs();
      const out: Record<string, FieldJob[]> = {};
      for (const r of rows) {
        if (!r.field_id) continue;
        const list = out[r.field_id];
        if (list) list.push(r);
        else out[r.field_id] = [r];
      }
      return out;
    },
  });
  const { data } = q;
  useEffect(() => {
    if (!data) {
      prevRef.current = undefined;
      generation.current += 1;
      return;
    }
    // Active-only: a finished job leaves the list instead of changing status.
    // Confirm `complete` on the field read before refreshing. A cancel or a
    // failure drops the row too, and those did not change the field's data.
    const left = jobsThatLeftActiveSet(prevRef.current, data);
    prevRef.current = data;
    if (!left.length) return;
    const gen = generation.current;
    void refreshConfirmedCompletions(
      qc,
      left,
      (fieldId) => api.fetchFieldJobs(fieldId),
      () => gen === generation.current,
    );
  }, [data, qc]);
  return q;
}

// System Dashboard — global admins One query per panel ON PURPOSE: these surfaces ship
// independently (three separate agent requests), so a panel whose endpoint doesn't exist yet must
// go "unavailable" alone rather than taking the dashboard down. `retry: false` everywhere — a
// 403/404 is an answer, not a transient to hammer.
const adminQ = { retry: false as const, staleTime: 15_000 };

export function useSystemCounts(enabled: boolean) {
  return useQuery({
    queryKey: qk.systemCounts,
    queryFn: () => api.fetchSystemCounts(),
    enabled,
    ...adminQ,
  });
}
export function useAdminUsers(enabled: boolean, orgId: string | null) {
  return useQuery({
    queryKey: [...qk.adminUsers, orgId ?? "all"] as const,
    queryFn: () => api.fetchAdminUsers(orgId ? { org_id: orgId } : {}),
    enabled,
    ...adminQ,
  });
}
export function useAdminApiKeys(enabled: boolean) {
  return useQuery({
    queryKey: qk.adminApiKeys,
    queryFn: () => api.fetchAdminApiKeys(),
    enabled,
    ...adminQ,
  });
}
export function useAdminApiScopes(enabled: boolean) {
  return useQuery({
    queryKey: qk.adminApiScopes,
    queryFn: () => api.fetchAdminApiScopes(),
    enabled,
    ...adminQ,
    staleTime: Infinity,
  });
}
export function useAdminApiKeyImpact(
  keyId: string | null,
  orgId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...qk.adminApiKeys, "impact", keyId, orgId ?? "all"] as const,
    queryFn: () =>
      api.fetchAdminApiKeyImpact(keyId as string, orgId ?? undefined),
    enabled: enabled && keyId != null,
    ...adminQ,
    staleTime: 0,
  });
}
function invalidateAdminKeys(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: qk.adminApiKeys });
}
export function useCreateAdminApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      scopes: string[];
      org_ids: string[];
      max_orders_per_day: number;
    }) => api.createAdminApiKey(body),
    gcTime: 0,
    onSettled: () => invalidateAdminKeys(qc),
  });
}
export function useRotateAdminApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.rotateAdminApiKey(keyId),
    gcTime: 0,
    onSettled: () => invalidateAdminKeys(qc),
  });
}
export function useRevokeAdminApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.revokeAdminApiKey(keyId),
    onSettled: () => invalidateAdminKeys(qc),
  });
}
export function useLinkAdminApiKeyOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, orgId }: { keyId: string; orgId: string }) =>
      api.linkAdminApiKeyOrg(keyId, orgId),
    onSettled: () => invalidateAdminKeys(qc),
  });
}
export function useUnlinkAdminApiKeyOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, orgId }: { keyId: string; orgId: string }) =>
      api.unlinkAdminApiKeyOrg(keyId, orgId),
    onSettled: () => invalidateAdminKeys(qc),
  });
}
export function useSetAdminApiKeyScopes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, scopes }: { keyId: string; scopes: string[] }) =>
      api.setAdminApiKeyScopes(keyId, scopes),
    onSettled: () => invalidateAdminKeys(qc),
  });
}
export function useAdminOrgs(enabled: boolean) {
  return useQuery({
    queryKey: qk.adminOrgs,
    queryFn: () => api.fetchAdminOrgs(),
    enabled,
    ...adminQ,
  });
}
export function useOpsManager(enabled: boolean) {
  return useQuery({
    queryKey: qk.opsManager,
    queryFn: () => api.fetchOpsManager(),
    enabled,
    refetchInterval: 15_000,
    ...adminQ,
  });
}
export function useOpsProcesses(enabled: boolean) {
  return useQuery({
    queryKey: qk.opsProcesses,
    queryFn: () => api.fetchOpsProcesses(),
    enabled,
    ...adminQ,
  });
}

export function useAdminJobs(
  enabled: boolean,
  view: "active" | "recent" = "active",
) {
  return useQuery({
    queryKey: [...qk.adminJobs, view] as const,
    queryFn: () => api.fetchAdminJobs(view),
    enabled,
    // only the live queue needs polling; a history view re-fetching every 15 s would fight the user
    refetchInterval: view === "active" ? 15_000 : false,
    ...adminQ,
  });
}

/*
 *
 * The signed-in user's identity + `is_global_admin` (`GET /users/me`).
 *
 * WHY A QUERY AND NOT THE SESSION: BE-Light reads the admin flag from the DB per request and never bakes
 * it into the token, so revoking admin takes effect on the next request. Reading it off the login-time
 * `Session` would both reintroduce that staleness AND — the bug this fixes — never populate at all, since
 * nothing re-fetches the session. Short staleTime so a granted/revoked admin surfaces without a re-login.
 */
export function useMe(enabled = true) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => api.fetchMe(),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}
